import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { I } from './kit';
import * as notifsApi from '../api/notifications';

// The bell sits in all five tab-root headers (Home, Sales, Listings, Earnings,
// More), so the unread count is cached module-level and shared: switching tabs
// re-uses the last value instead of hitting the endpoint on every mount.
//
// **Fetched once per app launch** (2026-08-17, by request — see the admin
// portal's NotificationBell for the same decision). There is no interval and no
// staleness window: the count is read the first time a bell mounts and then
// never again on its own. It still updates at the moments it would otherwise be
// visibly wrong, because those are user-caused — marking one or all read calls
// refreshUnreadCount() explicitly from NotifsPage.
//
// The trade this accepts: a notification arriving while the app is open leaves
// the badge stale until the next launch. Push handles that case; the badge is a
// reminder, not the delivery mechanism.
let cachedCount = null;
// Dedupes the launch fetch — all five tab-root bells can mount in the same tick,
// and without this each would fire its own request.
let launchFetch = null;
const subscribers = new Set();

const publish = (n) => {
  cachedCount = n;
  subscribers.forEach((set) => set(n));
};

/** Re-read the badge count. Call after marking notifications read. Never throws. */
export const refreshUnreadCount = async () => {
  try {
    const r = await notifsApi.listNotificationsUnreadCount();
    const d = r?.data ?? r;
    publish(Number(d?.count) || 0);
  } catch (_) {
    // the badge is cosmetic — a failed count must never surface an error
  }
};

/**
 * Drop the cached badge — call on logout, so the next agent to sign in on this
 * device does not inherit the previous one's count. Same reason
 * resetPerformanceScopes() exists: logout does not reload the page, so
 * module-level state survives it.
 */
export const resetUnreadCount = () => {
  cachedCount = null;
  launchFetch = null;
  subscribers.forEach((set) => set(0));
};

/** Shared unread-count subscription — also drives the More grid's badge dot. */
export function useUnreadCount() {
  const [count, setCount] = useState(cachedCount ?? 0);
  useEffect(() => {
    subscribers.add(setCount);
    // Only if it has never been read this launch. A failed fetch clears the
    // guard rather than caching the failure, so the next bell to mount retries
    // instead of leaving the badge dark for the whole session.
    if (cachedCount === null && !launchFetch) {
      launchFetch = refreshUnreadCount().finally(() => { launchFetch = null; });
    }
    return () => subscribers.delete(setCount);
  }, []);
  return count;
}

// The home header's icon button — the shape every tab-root header now uses.
const iconBtn = { width: 36, height: 36, borderRadius: 10, background: 'var(--surface2)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--muted)', flex: 'none' };

/**
 * Notification bell for a tab-root header. Safe to drop into any header — it
 * renders nothing on the notifications screen itself.
 */
export default function NotifBell() {
  const nav = useNavigate();
  const { pathname } = useLocation();
  const unread = useUnreadCount();

  if (pathname === '/notifs') return null;

  return (
    <div
      onClick={() => nav('/notifs')}
      role="button"
      aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
      style={{ ...iconBtn, position: 'relative' }}
    >
      <I.bell width={17} height={17} />
      {unread > 0 && <span style={{ position: 'absolute', top: 7, right: 7, width: 8, height: 8, borderRadius: 50, background: 'var(--bad)', border: '2px solid var(--surface2)' }} />}
    </div>
  );
}
